/**
 * Meta Cloud API — Facebook SDK Configuration
 *
 * Uses FB.login() popup (Embedded Signup) instead of redirect-based OAuth.
 * The config_id comes from Meta's WhatsApp Embedded Signup configuration.
 *
 * META_APP_ID and META_CONFIG_ID are defined per environment
 * in the vite.config.{local|dev|prod}.ts `define` blocks,
 * following the same pattern as URL_API_PLICK, FIREBASE_API_KEY, etc.
 *
 * See: https://developers.facebook.com/docs/whatsapp/embedded-signup
 */

/* eslint-disable no-var */
declare const META_APP_ID: string;
declare const META_CONFIG_ID: string;
/* eslint-enable no-var */

const META_SDK_VERSION = 'v25.0';

/**
 * Load the Facebook SDK asynchronously.
 * Idempotent: if already loaded, resolves immediately.
 * @returns {Promise<void>} Resolves when SDK is ready
 */
export const loadFacebookSdk = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.FB) {
      resolve();
      return;
    }

    // Init callback
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: false,
        version: META_SDK_VERSION,
      });
      resolve();
    };

    // Inject SDK script if not already present
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
      document.head.appendChild(script);
    }
  });
};

/**
 * Launch the Meta Embedded Signup popup via FB.login().
 *
 * Returns the auth code on success, or null if the user cancels.
 * @returns {Promise<string | null>} Auth code or null
 */
export const launchEmbeddedSignup = (): Promise<string | null> => {
  return new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('Facebook SDK not loaded'));
      return;
    }

    const loginOptions = {
      config_id: META_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
    };

    // eslint-disable-next-line no-console
    console.log('[Meta Cloud API] FB.login options:', loginOptions);

    window.FB.login((response: { authResponse?: { code?: string } }) => {
      // eslint-disable-next-line no-console
      console.log('[Meta Cloud API] FB.login response:', response);
      if (response.authResponse?.code) {
        resolve(response.authResponse.code);
      } else {
        // User cancelled or closed the popup
        resolve(null);
      }
    }, loginOptions);
  });
};

// Extend window for Facebook SDK types
declare global {
  interface Window {
    FB: {
      init: (params: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
      login: (
        callback: (response: { authResponse?: { code?: string } }) => void,
        options: { config_id: string; response_type: string; override_default_response_type: boolean },
      ) => void;
    };
    fbAsyncInit: () => void;
  }
}
